import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { Button, Dialog, PageHeader, Select, TextField, PlusSvg } from 'Components/ui';
import { PageHelper } from 'Components/PageHelper';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { Controller, useForm } from 'react-hook-form';
import { InputLoader } from 'Components/PageLoader/Input';
import { callDeleteAction, callArchiveAction, callUnarchiveAction } from './actions';
import {
  InfoBanner, InfoBannerTitle, InfoBannerDesc,
  FilterCard, FilterCardLabel, FilterRow1, FilterRow2,
  FilterField, FilterFieldLabel,
  FilterActions, FilterClearBtn, FilterSearchBtn,
  TableCard, ProcessTable, ProcessTh, ProcessTr, ProcessTd,
  TdName, TdDesc, TdCenter,
  CritBadge, BoolBadge, ActionBtns, ActionBtn,
  TableFooter, TableFooterInfo, PaginationBtns, PageBtn,
} from './styles';
import { getProcessesShortPaged } from 'services/processes-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { getUsersName } from 'services/users-service';

const InfoSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const SearchSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const ViewSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const EditSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const DeleteSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const ArchiveSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
);

const UnarchiveSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/>
    <polyline points="10 12 12 10 14 12"/><line x1="12" y1="10" x2="12" y2="16"/>
  </svg>
);

const PrevSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const NextSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const CRIT_STYLES = {
  '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
};

export const Processes = () => {
  const { t } = useTranslation('mapping');
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, getValues } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      description: '',
      user: '',
      dpia: 'all',
      lia: 'all',
      archived: false,
    },
  });

  const TRI_TO_BACKEND = { all: 'Todos', yes: 'Sim', no: 'Não' };

  const getContentSearch = () => {
    const dados = getValues();
    const dpiaParam = TRI_TO_BACKEND[dados.dpia] ?? 'Todos';
    const liaParam = TRI_TO_BACKEND[dados.lia] ?? 'Todos';
    let queryString = `&archived=${dados.archived}&dpia=${dpiaParam}&lia=${liaParam}`;
    if (dados.name !== '') queryString += `&name=${dados.name}`;
    if (dados.description !== '') queryString += `&description=${dados.description}`;
    if (dados.user !== '') queryString += `&user=${dados.user}`;
    if (dados.criticity !== '') queryString += `&criticity=${dados.criticity}`;
    return queryString;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { isLoading, error, data: pagedProcesses } = useQuery(['getProcesses', refetch, page], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const queryContent = getContentSearch();
    const result = await getProcessesShortPaged(selectedTenant.slug, page, 15, queryContent);
    if (result?.data.data.length > 0) return result?.data;
    return { data: [], page, totalPages: 0 };
  });

  const onDelete = item => { setItemToDelete(item); setOpenDeleteModal(true); };
  const onCloseDeleteModal = () => { setOpenDeleteModal(false); setItemToDelete(null); };

  const onArchive = item => { setItemToArchive(item); setOpenArchiveModal(true); };
  const onCloseArchiveModal = () => { setOpenArchiveModal(false); setItemToArchive(null); };

  const onUnarchive = item => { setItemToUnarchive(item); setOpenUnarchiveModal(true); };
  const onCloseUnarchiveModal = () => { setOpenUnarchiveModal(false); setItemToUnarchive(null); };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setRefetch, t);
    } catch (error) {
      onCloseDeleteModal();
      Dialog.alert({ title: t('processes.toasts.errorTitle'), message: error.response.data.message ?? error.message, icon: 'error' });
    }
  };

  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, onCloseArchiveModal, refetch, setRefetch, t);
    } catch (error) {
      onCloseArchiveModal();
      Dialog.alert({ title: t('processes.toasts.errorTitle'), message: error.response.data.message ?? error.message, icon: 'error' });
    }
  };

  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, onCloseUnarchiveModal, refetch, setRefetch, t);
    } catch (error) {
      onCloseUnarchiveModal();
      Dialog.alert({ title: t('processes.toasts.errorTitle'), message: error.response.data.message ?? error.message, icon: 'error' });
    }
  };

  const getCritStyle = level => CRIT_STYLES[level] || { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
  const getCritLabel = level => {
    const labels = {
      '1': t('processes.criticality.short.1', 'Muito Baixa'),
      '2': t('processes.criticality.short.2', 'Baixa'),
      '3': t('processes.criticality.short.3', 'Média'),
      '4': t('processes.criticality.short.4', 'Alta'),
      '5': t('processes.criticality.short.5', 'Muito Alta'),
    };
    return labels[level] || level;
  };

  const totalPages = pagedProcesses?.totalPages ?? 0;
  const pageItems = pagedProcesses?.data ?? [];

  const renderPageBtns = () => {
    const btns = [];
    if (page > 1) btns.push(<PageBtn key="prev" onClick={() => setPage(p => p - 1)}><PrevSvg /></PageBtn>);
    for (let i = 1; i <= totalPages; i++) {
      if (totalPages <= 7 || Math.abs(i - page) <= 2 || i === 1 || i === totalPages) {
        btns.push(<PageBtn key={i} $active={i === page} onClick={() => setPage(i)}>{i}</PageBtn>);
      } else if (Math.abs(i - page) === 3) {
        btns.push(<PageBtn key={`ell-${i}`} disabled style={{ color: 'var(--vo-slate)' }}>…</PageBtn>);
      }
    }
    if (page < totalPages) btns.push(<PageBtn key="next" onClick={() => setPage(p => p + 1)}><NextSvg /></PageBtn>);
    return btns;
  };

  return (
    <Container>
      <PageHeader
        data-tour="page-title"
        title={t('processes.list.title')}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('processes.list.title') },
        ]}
        actions={
          <Button data-tour="add-button" onClick={() => navigate('/processes/add')} startIcon={<PlusSvg />}>
            {t('mapping:processes.list.addButton')}
          </Button>
        }
      />

      <div data-tour="info-card">
        <InfoBanner>
          <InfoSvg />
          <div>
            <InfoBannerTitle>{t('processes.list.cardTitle')}</InfoBannerTitle>
            <InfoBannerDesc>{t('processes.list.description')}</InfoBannerDesc>
          </div>
        </InfoBanner>
      </div>

      {!error && (
        <div data-tour="filter-card">
          <FilterCard>
            <FilterCardLabel>{t('processes.list.filters.filtersLabel', 'Filtros')}</FilterCardLabel>
            <FilterRow1>
              <FilterField data-tour="filter-name">
                <FilterFieldLabel>{t('processes.list.filters.name')}</FilterFieldLabel>
                <TextField fullWidth {...register('name')} placeholder={t('processes.list.filters.namePlaceholder', 'Buscar por nome...')} />
              </FilterField>
              <FilterField data-tour="filter-criticity">
                <FilterFieldLabel>{t('processes.list.filters.criticality')}</FilterFieldLabel>
                <Controller
                  control={control}
                  name="criticity"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="criticity"
                      value={value}
                      onChange={onChange}
                      options={[
                        { value: '', label: t('processes.list.filters.selectCriticality') },
                        ...['1','2','3','4','5'].map(item => ({ value: item, label: t(`processes.criticality.${item}`, item) })),
                      ]}
                    />
                  )}
                />
              </FilterField>
              <FilterField data-tour="filter-user">
                {!userLoading ? (
                  <>
                    <FilterFieldLabel>{t('processes.list.filters.responsible')}</FilterFieldLabel>
                    <Controller
                      control={control}
                      name="user"
                      render={({ field: { onChange, value } }) => (
                        <Select
                          name="user"
                          value={value}
                          onChange={onChange}
                          options={[
                            { value: '', label: t('processes.list.filters.selectResponsible') },
                            ...(users || []).map(u => ({ value: u.name, label: u.name })),
                          ]}
                        />
                      )}
                    />
                  </>
                ) : (
                  <>
                    <FilterFieldLabel>{t('processes.list.filters.responsible')}</FilterFieldLabel>
                    <InputLoader height="40px" />
                  </>
                )}
              </FilterField>
            </FilterRow1>
            <FilterRow2>
              <FilterField data-tour="filter-dpia">
                <FilterFieldLabel>DPIA</FilterFieldLabel>
                <Controller
                  control={control}
                  name="dpia"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="dpia"
                      value={value}
                      onChange={onChange}
                      options={['all', 'yes', 'no'].map(item => ({ value: item, label: t(`processes.list.filters.options.${item}`) }))}
                    />
                  )}
                />
              </FilterField>
              <FilterField data-tour="filter-lia">
                <FilterFieldLabel>LIA</FilterFieldLabel>
                <Controller
                  control={control}
                  name="lia"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="lia"
                      value={value}
                      onChange={onChange}
                      options={['all', 'yes', 'no'].map(item => ({ value: item, label: t(`processes.list.filters.options.${item}`) }))}
                    />
                  )}
                />
              </FilterField>
              <FilterField data-tour="filter-archived">
                <FilterFieldLabel>{t('processes.list.filters.status', 'Status')}</FilterFieldLabel>
                <Controller
                  control={control}
                  name="archived"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="archived"
                      value={value ? 'archived' : 'active'}
                      onChange={(v) => onChange(v === 'archived')}
                      options={[
                        { value: 'active', label: t('processes.list.filters.statusActive', 'Ativo') },
                        { value: 'archived', label: t('processes.list.filters.archived') },
                      ]}
                    />
                  )}
                />
              </FilterField>
            </FilterRow2>
            <FilterActions data-tour="filter-buttons">
              <FilterClearBtn type="button" onClick={() => { reset(); setRefetch(!refetch); }}>
                {t('processes.list.filters.clear', 'Limpar')}
              </FilterClearBtn>
              <FilterSearchBtn type="button" onClick={() => setRefetch(!refetch)}>
                <SearchSvg />
                {t('processes.list.filters.filter')}
              </FilterSearchBtn>
            </FilterActions>
          </FilterCard>
        </div>
      )}

      {isLoading && <PageLoaderList />}
      {error && <div>{t('processes.list.errorLoad')}</div>}

      {!isLoading && !error && pageItems.length === 0 && (
        <NoData description={t('processes.list.empty')} />
      )}

      {!isLoading && !error && pageItems.length > 0 && (
        <>
          <div data-tour="table">
            <TableCard>
              <ProcessTable>
                <thead>
                  <tr>
                    <ProcessTh>{t('processes.table.name')}</ProcessTh>
                    <ProcessTh>{t('processes.list.filters.description')}</ProcessTh>
                    <ProcessTh>{t('processes.table.criticality')}</ProcessTh>
                    <ProcessTh>{t('processes.table.responsible')}</ProcessTh>
                    <ProcessTh $center>DPIA</ProcessTh>
                    <ProcessTh $center>LIA</ProcessTh>
                    <ProcessTh $center>{t('processes.table.actions', 'Ações')}</ProcessTh>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((item, idx) => {
                    const cs = getCritStyle(item.criticity);
                    return (
                      <ProcessTr key={item._id ?? idx}>
                        <TdName>{item.name}</TdName>
                        <TdDesc title={item.description}>{item.description}</TdDesc>
                        <ProcessTd>
                          {item.criticity ? (
                            <CritBadge $color={cs.color} $bg={cs.bg}>
                              {getCritLabel(item.criticity)}
                            </CritBadge>
                          ) : '-'}
                        </ProcessTd>
                        <ProcessTd>{item.user}</ProcessTd>
                        <TdCenter>
                          <BoolBadge $yes={!!item.dpia}>
                            {item.dpia ? t('processes.list.filters.options.yes') : t('processes.list.filters.options.no')}
                          </BoolBadge>
                        </TdCenter>
                        <TdCenter>
                          <BoolBadge $yes={!!item.lia}>
                            {item.lia ? t('processes.list.filters.options.yes') : t('processes.list.filters.options.no')}
                          </BoolBadge>
                        </TdCenter>
                        <TdCenter>
                          <ActionBtns>
                            <ActionBtn title={t('common:tooltips.view')} onClick={() => navigate(`/processes/show/${item._id}`)}>
                              <ViewSvg />
                            </ActionBtn>
                            <ActionBtn title={t('common:tooltips.edit')} onClick={() => navigate(`/processes/edit/${item._id}`)}>
                              <EditSvg />
                            </ActionBtn>
                            {!item.archived ? (
                              <ActionBtn title={t('common:tooltips.archive')} onClick={() => onArchive(item)}>
                                <ArchiveSvg />
                              </ActionBtn>
                            ) : (
                              <ActionBtn title={t('common:tooltips.unarchive')} onClick={() => onUnarchive(item)}>
                                <UnarchiveSvg />
                              </ActionBtn>
                            )}
                            <ActionBtn $danger title={t('common:tooltips.delete')} onClick={() => onDelete(item)}>
                              <DeleteSvg />
                            </ActionBtn>
                          </ActionBtns>
                        </TdCenter>
                      </ProcessTr>
                    );
                  })}
                </tbody>
              </ProcessTable>
              <TableFooter data-tour="pagination">
                <TableFooterInfo>
                  {t('processes.list.showing', { count: pageItems.length, total: pagedProcesses.total ?? pageItems.length })}
                </TableFooterInfo>
                <PaginationBtns>
                  {renderPageBtns()}
                </PaginationBtns>
              </TableFooter>
            </TableCard>
          </div>

          <Dialog
            open={openDeleteModal}
            onConfirm={onConfirmDeleteModal}
            onCancel={onCloseDeleteModal}
            message={t('processes.list.modals.deleteMessage', { name: itemToDelete?.name ?? '' })}
          />
          <Dialog
            open={openArchiveModal}
            onConfirm={onConfirmArchiveModal}
            onCancel={onCloseArchiveModal}
            message={t('processes.list.modals.archiveMessage', { name: itemToArchive?.name ?? '' })}
          />
          <Dialog
            open={openUnarchiveModal}
            onConfirm={onConfirmUnarchiveModal}
            onCancel={onCloseUnarchiveModal}
            message={t('processes.list.modals.unarchiveMessage', { name: itemToUnarchive?.name ?? '' })}
          />
        </>
      )}

      <PageHelper steps={tourSteps} tourKey="processes-list" />
    </Container>
  );
};

const tourSteps = [
  { target: 'body', content: 'Bem-vindo à página de Processos! Aqui você pode visualizar, filtrar e gerenciar todos os processos da organização.', placement: 'center', disableBeacon: true },
  { target: '[data-tour="page-title"]', content: 'Clique aqui no título para voltar à lista de processos.', disableScrolling: true },
  { target: '[data-tour="add-button"]', content: 'Clique no botão "+" para adicionar um novo processo à sua organização.', disableScrolling: true },
  { target: '[data-tour="info-card"]', content: 'Este card explica a importância de mapear os processos que tratam dados pessoais e impactam os objetivos de negócio.', disableScrolling: true },
  { target: '[data-tour="filter-card"]', content: 'Use este painel de filtros para encontrar processos específicos.', disableScrolling: true },
  { target: '[data-tour="filter-name"]', content: 'Filtre processos pelo nome.' },
  { target: '[data-tour="filter-criticity"]', content: 'Filtre por criticidade: de 1 (Muito Baixa) a 5 (Muito Alta). As cores ajudam na identificação visual.' },
  { target: '[data-tour="filter-user"]', content: 'Filtre processos por responsável.' },
  { target: '[data-tour="filter-dpia"]', content: 'DPIA (Data Protection Impact Assessment) - Filtre processos que possuem ou não avaliação de impacto à proteção de dados.' },
  { target: '[data-tour="filter-lia"]', content: 'LIA (Legitimate Interest Assessment) - Filtre processos que possuem ou não avaliação de interesse legítimo.' },
  { target: '[data-tour="filter-archived"]', content: 'Filtre por status: Ativo ou Arquivado.' },
  { target: '[data-tour="filter-buttons"]', content: 'Use "Limpar" para resetar todos os filtros ou "Filtrar" para aplicar os critérios selecionados.' },
  { target: '[data-tour="table"]', content: 'Esta tabela exibe todos os processos. Você pode visualizar detalhes, editar, arquivar ou excluir cada processo.' },
  { target: '[data-tour="pagination"]', content: 'Use a paginação para navegar entre as páginas de processos.' },
  { target: 'body', content: 'Tour concluído! Agora você conhece todas as funcionalidades da página de Processos.', placement: 'center' },
];
